import { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box, Button, Checkbox, Chip, Dialog, DialogContent,
  FormControlLabel, IconButton, Radio,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { usersAdminApi } from '../../lib/api/usersAdminApi'
import { lobsApi } from '../../lib/api/tenantsApi'

// Converts a Date to a YYYY-MM-DD string using the user's LOCAL timezone,
function toLocalDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
import { themeColors } from '../../app/layout/theme'
import { FormInput, SearchBar } from '../ui'
import { EntityDrawerInput } from './EntityFormDrawer'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CheckboxFilterDef {
  type: 'checkbox'
  key: string
  label: string
  options: { label: string; value: string }[]
  singleSelect?: boolean
}

export interface DateFilterDef {
  type: 'date'
  label: string
}

export interface OwnerFilterDef {
  type: 'owner'
  label: string
}

export interface PriceFilterDef {
  type: 'price'
  label: string
}

export type PriceOperator = 'lt' | 'gt' | 'eq' | 'lt_gt' | 'lt_eq' | 'gt_eq'

export interface PriceFilter {
  operator: PriceOperator | ''
  value1: string   // primary amount
  value2: string   // secondary amount (used for lt_gt: between two values)
}

export type FilterDef = CheckboxFilterDef | DateFilterDef | OwnerFilterDef | PriceFilterDef

export interface UnifiedFilters {
  checkboxes:  Record<string, string[]>
  datePreset:  '' | 'TODAY' | 'YESTERDAY' | 'LAST_7' | 'A_MONTH_AGO' | '3_MONTHS' | '6_MONTHS' | '1_YEAR' | 'CUSTOM'
  dateFrom:    string
  dateTo:      string
  ownerIds:    string[]
  price:       PriceFilter
}

export const EMPTY_PRICE_FILTER: PriceFilter = { operator: '', value1: '', value2: '' }

export const EMPTY_UNIFIED_FILTERS: UnifiedFilters = {
  checkboxes: {},
  datePreset: '',
  dateFrom:   '',
  dateTo:     '',
  ownerIds:   [],
  price:      EMPTY_PRICE_FILTER,
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getDefKey(def?: FilterDef): string {
  if (!def) return ''
  return ('key' in def && def.key) ? def.key : def.type
}

export function countActiveFilters(f: UnifiedFilters): number {
  let n = 0
  if (!f) return 0;
  for (const vals of Object.values(f.checkboxes || {})) n += vals.length
  if (f.datePreset) n += 1
  n += (f.ownerIds || []).length
  if (f.price?.operator) n += 1
  return n
}

export function dateRangeForPreset(preset: string): { from: Date; to: Date } {
  const now = new Date()
  if (preset === 'TODAY') {
    const s = new Date(now); s.setHours(0, 0, 0, 0)
    const e = new Date(now); e.setHours(23, 59, 59, 999)
    return { from: s, to: e }
  }
  if (preset === 'LAST_7') {
    const s = new Date(now); s.setDate(s.getDate() - 6); s.setHours(0, 0, 0, 0)
    const e = new Date(now); e.setHours(23, 59, 59, 999)
    return { from: s, to: e }
  }
  if (preset === 'YESTERDAY') {
    const s = new Date(now); s.setDate(s.getDate() - 1); s.setHours(0, 0, 0, 0)
    const e = new Date(now); e.setDate(e.getDate() - 1); e.setHours(23, 59, 59, 999)
    return { from: s, to: e }
  }
  if (preset === 'A_MONTH_AGO') {
    const s = new Date(now); s.setMonth(s.getMonth() - 1); s.setHours(0, 0, 0, 0)
    const e = new Date(now); e.setHours(23, 59, 59, 999)
    return { from: s, to: e }
  }
  if (preset === '3_MONTHS') {
    const s = new Date(now); s.setMonth(s.getMonth() - 3); s.setHours(0, 0, 0, 0)
    const e = new Date(now); e.setHours(23, 59, 59, 999)
    return { from: s, to: e }
  }
  if (preset === '6_MONTHS') {
    const s = new Date(now); s.setMonth(s.getMonth() - 6); s.setHours(0, 0, 0, 0)
    const e = new Date(now); e.setHours(23, 59, 59, 999)
    return { from: s, to: e }
  }
  if (preset === '1_YEAR') {
    const s = new Date(now); s.setFullYear(s.getFullYear() - 1); s.setHours(0, 0, 0, 0)
    const e = new Date(now); e.setHours(23, 59, 59, 999)
    return { from: s, to: e }
  }
  return { from: new Date(0), to: new Date(8640000000000000) }
}

export function applyUnifiedDateFilter(iso: string, filters: UnifiedFilters): boolean {
  if (!filters.datePreset) return true
  const d = new Date(iso)
  if (filters.datePreset === 'CUSTOM') {
    if (filters.dateFrom && d < new Date(filters.dateFrom)) return false
    if (filters.dateTo   && d > new Date(filters.dateTo + 'T23:59:59')) return false
    return true
  }
  const { from, to } = dateRangeForPreset(filters.datePreset)
  return d >= from && d <= to
}

// ─── Mini calendar ────────────────────────────────────────────────────────────

const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']

interface MiniCalendarProps {
  year:    number
  month:   number  // 0-based
  onNav:   (y: number, m: number) => void
  from:    Date | null
  to:      Date | null
  onSelect: (d: Date) => void
  selecting: 'from' | 'to'
}

function MiniCalendar({ year, month, onNav, from, to, onSelect, selecting }: MiniCalendarProps) {
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells: (number | null)[] = Array(firstDay).fill(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  function isInRange(d: number) {
    if (!from || !to) return false
    const dt = new Date(year, month, d)
    return dt > from && dt < to
  }
  function isFrom(d: number)  { return from ? new Date(year, month, d).toDateString() === from.toDateString() : false }
  function isTo(d: number)    { return to   ? new Date(year, month, d).toDateString() === to.toDateString()   : false }
  function isToday(d: number) { return new Date(year, month, d).toDateString() === new Date().toDateString() }

  return (
    <Box sx={{ width: 200 }}>
      {/* Nav */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <IconButton size="small" onClick={() => { const d = new Date(year, month - 1); onNav(d.getFullYear(), d.getMonth()) }}>
          <Typography sx={{ fontSize: 14, lineHeight: 1 }}>‹</Typography>
        </IconButton>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
          {MONTHS[month]} {year}
        </Typography>
        <IconButton size="small" onClick={() => { const d = new Date(year, month + 1); onNav(d.getFullYear(), d.getMonth()) }}>
          <Typography sx={{ fontSize: 14, lineHeight: 1 }}>›</Typography>
        </IconButton>
      </Box>

      {/* Day of week headers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
        {DOW.map(d => (
          <Typography key={d} sx={{ fontSize: 10, fontWeight: 500, color: '#9ca3af', textAlign: 'center', py: 0.25 }}>{d}</Typography>
        ))}
      </Box>

      {/* Days */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px' }}>
        {cells.map((day, i) => {
          if (!day) return <Box key={i} />
          const inRange = isInRange(day)
          const isF = isFrom(day)
          const isT = isTo(day)
          const isTd = isToday(day)
          return (
            <Box
              key={i}
              onClick={() => onSelect(new Date(year, month, day))}
              sx={{
                height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: isF || isT ? '50%' : '0',
                cursor: 'pointer',
                bgcolor: isF || isT
                  ? themeColors.primary
                  : inRange ? `${themeColors.primary}18` : 'transparent',
                '&:hover': { bgcolor: isF || isT ? themeColors.primaryDark : `${themeColors.primary}30` },
              }}
            >
              <Typography sx={{
                fontSize: 11,
                fontWeight: isF || isT || isTd ? 700 : 400,
                color: isF || isT ? '#fff' : isTd ? themeColors.primary : '#374151',
              }}>
                {day}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

// ─── Filter Dialog ────────────────────────────────────────────────────────────

interface Props {
  open:           boolean
  onClose:        () => void
  onApply:        (f: UnifiedFilters) => void
  filterDefs:     FilterDef[]
  initialFilters: UnifiedFilters
}

export function UnifiedFilterDialog({ open, onClose, onApply, filterDefs, initialFilters }: Props) {
  const [draft,        setDraft]       = useState<UnifiedFilters>(EMPTY_UNIFIED_FILTERS)
  const [activeKey,    setActiveKey]   = useState<string>('')
  const [ownerSearch,  setOwnerSearch] = useState('')
  const [optionSearch, setOptionSearch] = useState('')
  const [cal1,         setCal1]        = useState({ year: new Date().getFullYear(), month: new Date().getMonth() })
  const [cal2,         setCal2]        = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 <= 11 ? new Date().getMonth() + 1 : 0 })
  const [selecting,    setSelecting]   = useState<'from' | 'to'>('from')

  useEffect(() => { 
    if (open) {
      const base = initialFilters || EMPTY_UNIFIED_FILTERS
      // Normalise: merge with EMPTY so new fields (price, etc.) are always present
      // even when initialFilters comes from old sessionStorage without those fields
      setDraft({
        ...EMPTY_UNIFIED_FILTERS,
        ...base,
        price: base.price ?? EMPTY_PRICE_FILTER,
        ownerIds: base.ownerIds ?? [],
        checkboxes: base.checkboxes ?? {},
      })
      setActiveKey(filterDefs[0] ? getDefKey(filterDefs[0]) : '') 
      setOwnerSearch('')
      setOptionSearch('')
    } 
  }, [open, initialFilters, filterDefs])

  const { data: usersData } = useQuery({
    queryKey: ['users-for-filter'],
    queryFn: () => usersAdminApi.getAll({ pageSize: 200 }),
    staleTime: 120_000,
    enabled: open,
  })
  const allUsers = usersData?.items ?? []

  // ── Dynamic tenant cascade (draft-driven, no Apply needed) ──
  const draftOrgId = draft.checkboxes?.orgId?.[0]

  const { data: draftLobs = [] } = useQuery({
    queryKey: ['filter-lobs', draftOrgId],
    queryFn: () => lobsApi.getAll(draftOrgId!),
    enabled: open && !!draftOrgId,
    staleTime: 30_000,
  })

  // LOB → Users cascade: when a "userId" filter group is present, its options
  // narrow live to the users belonging to the LOB(s) ticked in this dialog —
  // no Apply needed (mirrors the Org → LOB cascade above).
  const draftLobIds = draft.checkboxes?.lobId ?? []

  function userOptionsForDraft(def: CheckboxFilterDef): { label: string; value: string }[] {
    const hasUnassigned = def.options.some(o => o.value === 'unassigned')
    const unassignedOpt = hasUnassigned ? [{ label: 'Unassigned', value: 'unassigned' }] : []
    if (draftLobIds.length) {
      const userOpts = allUsers
        .filter(u => u.lobId && draftLobIds.includes(u.lobId))
        .map(u => ({ label: u.name, value: u.id }))
      return [...unassignedOpt, ...userOpts]
    }
    // No LOB ticked → use the page-provided (already org-scoped) options,
    // falling back to the dialog's own user list.
    return def.options.length ? def.options : [...unassignedOpt, ...allUsers.map(u => ({ label: u.name, value: u.id }))]
  }

  const filteredUsers = ownerSearch
    ? allUsers.filter(u => u.name.toLowerCase().includes(ownerSearch.toLowerCase()))
    : allUsers

  function toggleCheckbox(key: string, value: string, singleSelect?: boolean) {
    setDraft(d => {
      const cur = (d.checkboxes && d.checkboxes[key]) ?? []
      let next: string[]
      if (singleSelect) {
        next = cur.includes(value) ? [] : [value]
      } else {
        next = cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value]
      }
      // When org changes, clear downstream LOB (and user) selections
      if (key === 'orgId') {
        return { ...d, checkboxes: { ...d.checkboxes, [key]: next, lobId: [], userId: [] } }
      }
      // When LOB changes, clear downstream user selections (they may no longer belong)
      if (key === 'lobId') {
        return { ...d, checkboxes: { ...d.checkboxes, [key]: next, userId: [] } }
      }
      return { ...d, checkboxes: { ...d.checkboxes, [key]: next } }
    })
  }

  function selectAll(def: CheckboxFilterDef) {
    setDraft(d => ({
      ...d, checkboxes: { ...d.checkboxes, [def.key]: def.options.map(o => o.value) }
    }))
  }

  function clearGroup(key: string) {
    setDraft(d => ({ ...d, checkboxes: { ...d.checkboxes, [key]: [] } }))
  }

  function toggleOwner(id: string) {
    setDraft(d => {
      const next = d.ownerIds.includes(id) ? d.ownerIds.filter(i => i !== id) : [...d.ownerIds, id]
      return { ...d, ownerIds: next }
    })
  }

  function removeChip(type: 'checkbox' | 'date' | 'owner', key: string, val?: string) {
    if (type === 'checkbox' && key && val !== undefined) {
      setDraft(d => ({ ...d, checkboxes: { ...d.checkboxes, [key]: (d.checkboxes[key] ?? []).filter(v => v !== val) } }))
    } else if (type === 'date') {
      setDraft(d => ({ ...d, datePreset: '', dateFrom: '', dateTo: '' }))
    } else if (type === 'owner' && val) {
      setDraft(d => ({ ...d, ownerIds: d.ownerIds.filter(i => i !== val) }))
    }
  }

  function handleDateSelect(d: Date) {
    if (selecting === 'from') {
      setDraft(prev => ({ ...prev, dateFrom: toLocalDateStr(d), dateTo: '' }))
      setSelecting('to')
    } else {
      if (draft.dateFrom && d < new Date(draft.dateFrom)) {
        setDraft(prev => ({ ...prev, dateFrom: toLocalDateStr(d), dateTo: '' }))
        setSelecting('to')
      } else {
        setDraft(prev => ({ ...prev, dateTo: toLocalDateStr(d) }))
        setSelecting('from')
      }
    }
  }

  function handleReset() { setDraft({ ...EMPTY_UNIFIED_FILTERS, price: EMPTY_PRICE_FILTER }) }

  const chips: { label: string; onRemove: () => void }[] = []
  for (const def of filterDefs) {
    if (def.type === 'checkbox') {
      const vals = (draft.checkboxes && draft.checkboxes[def.key]) ?? []
      for (const v of vals) {
        // For cascading filters, also look up the live-fetched options so names appear instead of IDs
        const livePool =
          def.key === 'lobId' ? draftLobs.map(l => ({ label: l.name, value: l.id }))
          : def.key === 'userId' ? [{ label: 'Unassigned', value: 'unassigned' }, ...allUsers.map(u => ({ label: u.name, value: u.id }))]
          : []
        const opt = def.options.find(o => o.value === v) ?? livePool.find(o => o.value === v)
        chips.push({ label: `${def.label}: ${opt?.label ?? v}`, onRemove: () => removeChip('checkbox', def.key, v) })
      }
    } else if (def.type === 'date' && draft.datePreset) {
      const label = draft.datePreset === 'TODAY' ? 'Date: Today'
        : draft.datePreset === 'YESTERDAY' ? 'Date: Yesterday'
        : draft.datePreset === 'LAST_7' ? 'Date: Last 7 Days'
        : draft.datePreset === 'A_MONTH_AGO' ? 'Date: A month ago'
        : draft.datePreset === '3_MONTHS' ? 'Date: 3 months'
        : draft.datePreset === '6_MONTHS' ? 'Date: 6 months'
        : draft.datePreset === '1_YEAR' ? 'Date: 1 year'
        : draft.dateFrom && draft.dateTo ? `Date: ${draft.dateFrom} → ${draft.dateTo}`
        : 'Date: Custom Range'
      chips.push({ label, onRemove: () => removeChip('date', '') })
    } else if (def.type === 'owner') {
      for (const id of draft.ownerIds || []) {
        const user = allUsers.find(u => u.id === id)
        chips.push({ label: `Owner: ${user?.name ?? id}`, onRemove: () => removeChip('owner', '', id) })
      }
    } else if (def.type === 'price' && draft.price?.operator) {
      const opLabel: Record<string, string> = {
        lt: 'Less Than', gt: 'Greater Than', eq: 'Equal To',
        lt_gt: 'Between', lt_eq: 'Less Than & Equal', gt_eq: 'Greater Than & Equal',
      }
      const op = opLabel[draft.price.operator] ?? draft.price.operator
      const val = draft.price.operator === 'lt_gt'
        ? `₹${draft.price.value1} – ₹${draft.price.value2}`
        : `₹${draft.price.value1}`
      chips.push({ label: `Price: ${op} ${val}`, onRemove: () => setDraft(d => ({ ...d, price: EMPTY_PRICE_FILTER })) })
    }
  }

  const activeDef = useMemo(() => {
    return filterDefs.find(def => getDefKey(def) === activeKey) ?? filterDefs[0]
  }, [filterDefs, activeKey])
  const activeDefKey = getDefKey(activeDef) || 'empty'
  const totalActive = countActiveFilters(draft)

  const fromDate = draft.dateFrom ? new Date(draft.dateFrom) : null
  const toDate   = draft.dateTo   ? new Date(draft.dateTo)   : null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      slotProps={{ backdrop: { sx: { backgroundColor: 'transparent' } } }}
      PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden', maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' } }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.75, borderBottom: '1px solid #f1f5f9' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 15, color: '#1e293b' }}>Filters</Typography>
          {totalActive > 0 && (
            <Box sx={{ minWidth: 20, height: 20, borderRadius: '10px', bgcolor: themeColors.primary,
              display: 'flex', alignItems: 'center', justifyContent: 'center', px: '6px' }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>{totalActive}</Typography>
            </Box>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#94a3b8' }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Active filter chips */}
      {chips.length > 0 && (
        <Box sx={{ px: 2.5, py: 1.25, borderBottom: '1px solid #f1f5f9', display: 'flex', flexWrap: 'wrap', gap: 0.75, maxHeight: 90, overflowY: 'auto' }}>
          {chips.map((c, i) => (
            <Chip
              key={i} label={c.label} size="small" onDelete={c.onRemove}
              sx={{
                fontSize: 11, fontWeight: 500, height: 22,
                bgcolor: '#f1f5f9', color: '#374151',
                '& .MuiChip-deleteIcon': { fontSize: 13, color: '#94a3b8', '&:hover': { color: '#ef4444' } },
              }}
            />
          ))}
        </Box>
      )}

      <DialogContent sx={{ p: 0, display: 'flex', flex: 1, minHeight: 320, overflow: 'hidden' }}>
        {/* Left sidebar */}
        <Box sx={{ width: 148, borderRight: '1px solid #f1f5f9', flexShrink: 0, py: 1, overflowY: 'auto' }}>
          {filterDefs.map((def) => {
            const defKey = getDefKey(def)
            const isActive = defKey === activeDefKey
            let hasDot = false
            if (def.type === 'checkbox') hasDot = ((draft.checkboxes && draft.checkboxes[def.key])?.length ?? 0) > 0
            else if (def.type === 'date') hasDot = !!draft.datePreset
            else if (def.type === 'owner') hasDot = (draft.ownerIds || []).length > 0

            return (
              <Box
                key={defKey}
                onClick={() => {
                  setActiveKey(defKey)
                  setOwnerSearch('')
                  setOptionSearch('')
                }}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: 2, py: 1.1, cursor: 'pointer',
                  bgcolor: isActive ? `${themeColors.primary}10` : 'transparent',
                  borderRight: isActive ? `3px solid ${themeColors.primary}` : '3px solid transparent',
                  '&:hover': { bgcolor: `${themeColors.primary}08` },
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? themeColors.primary : '#374151' }}>
                  {def.label}
                </Typography>
                {hasDot && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: themeColors.primary, flexShrink: 0 }} />}
              </Box>
            )
          })}
        </Box>

        {/* Right content */}
        <Box key={activeDefKey} sx={{ flex: 1, p: 2, overflowY: 'auto', pr: 1.5, '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 3 } }}>
          {activeDef?.type === 'checkbox' && (() => {
            const def = activeDef as CheckboxFilterDef
            // The Org→LOB cascade only applies when an Organisation filter is actually
            // present (Super Admin, who must pick an org before narrowing by LOB).
            // Org Admins have no Organisation filter at all — their org is fixed — so
            // the LOB options passed in via def.options (already scoped to their org)
            // should be used directly instead of waiting on a parent org selection.
            const hasOrgFilterDef = filterDefs.some(d => d.type === 'checkbox' && d.key === 'orgId')

            // For lobId, merge in live-fetched options over the static ones;
            // for userId, narrow live to users of the ticked LOB(s)
            const liveOptions =
              def.key === 'lobId' && hasOrgFilterDef
                ? (draftOrgId ? draftLobs.map(l => ({ label: l.name, value: l.id })) : def.options)
                : def.key === 'userId'
                  ? userOptionsForDraft(def)
                  : def.options
            const selected = (draft.checkboxes && draft.checkboxes[def.key]) ?? []
            const allSelected = selected.length === liveOptions.length && liveOptions.length > 0

            // Show a hint when a parent hasn't been selected yet (Super Admin cascade only)
            const needsParent = def.key === 'lobId' && hasOrgFilterDef && !draftOrgId

            const displayedOptions = optionSearch
              ? liveOptions.filter(o => o.label.toLowerCase().includes(optionSearch.toLowerCase()))
              : liveOptions

            return (
              <Box>
                {needsParent ? (
                  <Typography sx={{ fontSize: 13, color: '#9ca3af', py: 2, textAlign: 'center' }}>
                    Select an Organisation first
                  </Typography>
                ) : (
                  <>
                    {liveOptions.length > 5 && (
                      <SearchBar
                        key={activeDefKey}
                        value={optionSearch}
                        onSearch={setOptionSearch}
                        placeholder={`Search ${def.label}...`}
                        sx={{ mb: 1.5, width: '100%', boxSizing: 'border-box' }}
                      />
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      {!def.singleSelect ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small" checked={allSelected}
                              indeterminate={selected.length > 0 && !allSelected}
                              onChange={e => e.target.checked
                                ? setDraft(d => ({ ...d, checkboxes: { ...d.checkboxes, [def.key]: liveOptions.map(o => o.value) } }))
                                : clearGroup(def.key)
                              }
                              sx={{ p: 0.5, color: '#d1d5db', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: themeColors.primary } }}
                            />
                          }
                          label={<Typography sx={{ fontSize: 13, color: '#374151' }}>Select all</Typography>}
                          sx={{ m: 0, gap: 0.5 }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>Select one option</Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                          {selected.length}/{liveOptions.length}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: themeColors.primary, cursor: 'pointer', fontWeight: 500 }}
                          onClick={() => clearGroup(def.key)}>
                          Clear all
                        </Typography>
                      </Box>
                    </Box>

                    {displayedOptions.length === 0 ? (
                      <Typography sx={{ fontSize: 13, color: '#9ca3af', py: 2, textAlign: 'center' }}>
                        No options available
                      </Typography>
                    ) : (
                      <Box sx={{ maxHeight: 240, overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: 5 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 3 } }}>
                        {displayedOptions.map(opt => (
                          <FormControlLabel
                            key={`${activeDefKey}-${opt.value}`}
                            control={
                              def.singleSelect ? (
                                <Radio
                                  size="small" checked={selected.includes(opt.value)}
                                  onClick={() => toggleCheckbox(def.key, opt.value, true)}
                                  sx={{ p: 0.5, color: '#d1d5db', '&.Mui-checked': { color: themeColors.primary } }}
                                />
                              ) : (
                                <Checkbox
                                  size="small" checked={selected.includes(opt.value)}
                                  onChange={() => toggleCheckbox(def.key, opt.value)}
                                  sx={{ p: 0.5, color: '#d1d5db', '&.Mui-checked': { color: themeColors.primary } }}
                                />
                              )
                            }
                            label={<Typography sx={{ fontSize: 13, color: '#374151' }}>{opt.label}</Typography>}
                            sx={{ m: 0, gap: 0.5, display: 'flex', py: 0.3 }}
                          />
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )
          })()}

          {activeDef?.type === 'date' && (
            <Box>
              {(['TODAY', 'YESTERDAY', 'LAST_7', 'A_MONTH_AGO', '3_MONTHS', '6_MONTHS', '1_YEAR', 'CUSTOM'] as const).map(preset => {
                const label = preset === 'TODAY' ? 'Today' 
                  : preset === 'YESTERDAY' ? 'Yesterday'
                  : preset === 'LAST_7' ? 'Last 7 Days' 
                  : preset === 'A_MONTH_AGO' ? 'A month ago'
                  : preset === '3_MONTHS' ? '3 months'
                  : preset === '6_MONTHS' ? '6 months'
                  : preset === '1_YEAR' ? '1 year'
                  : 'Custom Range'
                return (
                  <FormControlLabel
                    key={preset}
                    control={
                      <Radio
                        size="small" checked={draft.datePreset === preset}
                        onChange={() => setDraft(d => ({ ...d, datePreset: preset, dateFrom: '', dateTo: '' }))}
                        sx={{ p: 0.5, color: '#d1d5db', '&.Mui-checked': { color: themeColors.primary } }}
                      />
                    }
                    label={<Typography sx={{ fontSize: 13, color: '#374151' }}>{label}</Typography>}
                    sx={{ m: 0, gap: 0.5, display: 'flex', py: 0.4 }}
                  />
                )
              })}

              {draft.datePreset === 'CUSTOM' && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Select Date Range
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                    <EntityDrawerInput
                      type="date"
                      value={draft.dateFrom}
                      onChange={v => setDraft(d => ({ ...d, dateFrom: v }))}
                      sx={{ flex: 1 }}
                    />
                    <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>→</Typography>
                    <EntityDrawerInput
                      type="date"
                      value={draft.dateTo}
                      onChange={v => setDraft(d => ({ ...d, dateTo: v }))}
                      sx={{ flex: 1 }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <MiniCalendar
                      year={cal1.year} month={cal1.month}
                      onNav={(y, m) => setCal1({ year: y, month: m })}
                      from={fromDate} to={toDate}
                      onSelect={handleDateSelect}
                      selecting={selecting}
                    />
                    <MiniCalendar
                      year={cal2.year} month={cal2.month}
                      onNav={(y, m) => setCal2({ year: y, month: m })}
                      from={fromDate} to={toDate}
                      onSelect={handleDateSelect}
                      selecting={selecting}
                    />
                  </Box>
                  <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 1 }}>
                    {selecting === 'from' ? 'Click to set start date' : 'Click to set end date'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {activeDef?.type === 'owner' && (
            <Box>
              <SearchBar
                value={ownerSearch}
                onSearch={setOwnerSearch}
                placeholder="Search Owners"
                sx={{ mb: 1.5, width: '100%', boxSizing: 'border-box' }}
              />
              <Box sx={{ maxHeight: 240, overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: 5 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 3 } }}>
                {filteredUsers.map(u => (
                  <FormControlLabel
                    key={u.id}
                    control={
                      <Checkbox
                        size="small" checked={(draft.ownerIds || []).includes(u.id)}
                        onChange={() => toggleOwner(u.id)}
                        sx={{ p: 0.5, color: '#d1d5db', '&.Mui-checked': { color: themeColors.primary } }}
                      />
                    }
                    label={<Typography sx={{ fontSize: 13, color: '#374151' }}>{u.name}</Typography>}
                    sx={{ m: 0, gap: 0.5, display: 'flex', py: 0.4 }}
                  />
                ))}
                {filteredUsers.length === 0 && (
                  <Typography sx={{ fontSize: 13, color: '#9ca3af', py: 2, textAlign: 'center' }}>No users found</Typography>
                )}
              </Box>
            </Box>
          )}

          {activeDef?.type === 'price' && (() => {
            const price = draft.price ?? EMPTY_PRICE_FILTER
            const PRICE_OPTIONS: { value: PriceOperator; label: string }[] = [
              { value: 'lt',    label: 'Less Than' },
              { value: 'gt',    label: 'Greater Than' },
              { value: 'eq',    label: 'Equal To' },
              { value: 'lt_gt', label: 'Less Than And Greater Than' },
              { value: 'lt_eq', label: 'Less Than And Equal' },
              { value: 'gt_eq', label: 'Greater Than And Equal' },
            ]
            const isBetween = price.operator === 'lt_gt'
            return (
              <Box>
                {PRICE_OPTIONS.map(opt => (
                  <FormControlLabel
                    key={opt.value}
                    control={
                      <Radio
                        size="small"
                        checked={price.operator === opt.value}
                        onChange={() => setDraft(d => ({ ...d, price: { ...EMPTY_PRICE_FILTER, operator: opt.value } }))}
                        sx={{ p: 0.5, color: '#d1d5db', '&.Mui-checked': { color: themeColors.primary } }}
                      />
                    }
                    label={<Typography sx={{ fontSize: 13, color: '#374151' }}>{opt.label}</Typography>}
                    sx={{ m: 0, gap: 0.5, display: 'flex', py: 0.4 }}
                  />
                ))}

                {price.operator && (
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {isBetween ? 'From Amount (₹)' : 'Amount (₹)'}
                      </Typography>
                      <EntityDrawerInput
                        type="number"
                        value={price.value1}
                        onChange={v => setDraft(d => ({ ...d, price: { ...d.price, value1: v } }))}
                        placeholder="e.g. 10000"
                      />
                    </Box>
                    {isBetween && (
                      <>
                        <Typography sx={{ color: '#94a3b8', fontSize: 16, mt: 2.5 }}>–</Typography>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            To Amount (₹)
                          </Typography>
                          <EntityDrawerInput
                            type="number"
                            value={price.value2}
                            onChange={v => setDraft(d => ({ ...d, price: { ...d.price, value2: v } }))}
                            placeholder="e.g. 50000"
                          />
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )
          })()}
        </Box>
      </DialogContent>

      {/* Footer */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderTop: '1px solid #f1f5f9' }}>
        <Button size="small" onClick={handleReset}
          sx={{ fontSize: 13, textTransform: 'none', color: '#64748b', fontWeight: 500 }}>
          Reset
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={onClose}
            sx={{ fontSize: 13, textTransform: 'none', borderRadius: '8px', borderColor: '#e2e8f0', color: '#374151' }}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={() => {
              const f = { ...draft }
              if (f.datePreset && f.datePreset !== 'CUSTOM') {
                const { from, to } = dateRangeForPreset(f.datePreset)
                f.dateFrom = toLocalDateStr(from)
                f.dateTo = toLocalDateStr(to)
              } else if (f.datePreset === 'CUSTOM') {
                f.dateFrom = draft.dateFrom
                f.dateTo = draft.dateTo
              }
              onApply(f);
              onClose()
            }}
              sx={{ fontSize: 13, textTransform: 'none', borderRadius: '8px',
                bgcolor: themeColors.primaryDark, '&:hover': { bgcolor: themeColors.primary }, boxShadow: 'none' }}>
              Apply Filters
            </Button>
        </Box>
      </Box>
    </Dialog>
  )
}
